import React from 'react';
import { Box, Typography } from '@mui/material';
import styles from './AISummaryDetails.module.scss';
import { AISummaryDetailsProps } from './types';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import { ActionButtons } from '../action-buttons/ActionButtons';
import { AISummaryStartForm } from './components/AISummaryStartForm';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAISummary } from '../../hooks/useAISummary';
import { useAISummaryDialog } from '../../../../common/ai-summary-dialog/useAISummaryDialog';
import AISummaryDialog from '../../../../common/ai-summary-dialog/AISummaryDialog';
import MessageModal from '../../../../common/message-modal/MessageModal';
import { ROUTES } from '../../../../common/constants/routes';
import { PacmanLoaderBlock } from '../../../../common/pacman-loader';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { RootState } from '../../../../store';

const AISummaryDetails = ({
  pendingStatus,
  generateAISummary,
  downloadDocument,
  signDocument,
  editDocument,
  createTemplate,
  deleteDocument,
  doc,
  handleStartAISummary,
  allowGettingAISummary,
}: AISummaryDetailsProps) => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const contractGenerated = params.get('contractGenerated');
  const generateSummaryParam = params.get('generateSummary');
  const { documentId } = useParams();
  const aiSummaryGenerated = useAppSelector(
    (state: RootState) => state.currentDocumentAISummary.summary
  );
  const aiAssistProp = contractGenerated
    ? 'open_ai_assist_document_contract'
    : 'open_ai_assist_document_upload';
  const aiAssistId = doc && doc[aiAssistProp].length > 0 ? Math.max(...doc[aiAssistProp]) : null;
  const navigate = useNavigate();
  const {
    summary,
    pendingStatus: pendingStatusAISummary,
    aiSummaryCreationMode,
    setAISummaryCreationMode,
  } = useAISummary(aiAssistId, allowGettingAISummary);
  const {
    dialogOpen: aiSummaryDialogOpen,
    setDialogOpen: setAISummaryDialogOpen,
    document: documentForAISummary,
    setDocument: setDocumentForAISummary,
    planUpgradingMessage: planUpgradingMessageAISummary,
    setPlanUpgradingMessage: setPlanUpgradingMessageAISummary,
    handleAnalyze: handleAnalyzeFunction,
    documentType,
    setDocumentType,
    purpose,
    setPurpose,
    purposes,
    resultFormat,
    setResultFormat,
    contextualInformation,
    setContextualInformation,
    simplifyJargon,
    setSimplifyJargon,
    detailLevel,
    setDetailLevel,
    analysisLoading,
    setAnalysisLoading,
  } = useAISummaryDialog();
  return (
    <>
      <Box className={styles.rightSection}>
        <Box className={styles.aiSummaryHeader}>
          <Typography className={styles.aiSummaryTitle} variant='h6'>
            {t('headers.aiSummary')}
          </Typography>
          <Box className={styles.headerActions}>
            <ActionButtons
              handleAISummaryGeneration={() => setAISummaryCreationMode(true)}
              handleDocumentDownload={downloadDocument}
              handleSignDocument={signDocument}
              handleEditDocument={editDocument}
              handleCreateTemplate={createTemplate}
              handleDeleteDocument={deleteDocument}
              isDocumentContentTab={false}
              doc={doc}
            />
          </Box>
        </Box>
        <Box className={styles.aiSummaryContent}>
          {(summary || aiSummaryGenerated) && !aiSummaryCreationMode ? (
            parse(aiSummaryGenerated?.content || summary)
          ) : !summary && !aiSummaryCreationMode ? (
            <AISummaryStartForm handleBtnClick={() => setAISummaryCreationMode(true)} />
          ) : null}
          {aiSummaryCreationMode && (
            <AISummaryDialog
              open={aiSummaryDialogOpen}
              //documentName={documentForAISummary.name}
              onClose={() => {
                setDocumentForAISummary(null);
                setAISummaryCreationMode(false);
                if (generateSummaryParam) {
                  navigate(window.location.pathname);
                }
              }}
              simplifyJargon={simplifyJargon}
              setSimplifyJargon={setSimplifyJargon}
              setDocumentType={setDocumentType}
              documentType={documentType}
              resultFormat={resultFormat}
              setResultFormat={setResultFormat}
              purpose={purpose}
              setPurpose={setPurpose}
              purposes={purposes}
              contextualInformation={contextualInformation}
              setContextualInformation={setContextualInformation}
              detailLevel={detailLevel}
              setDetailLevel={setDetailLevel}
              handleAnalyzeFunction={() =>
                handleAnalyzeFunction({
                  handleAnalyzeSuccess: () => {
                    setAISummaryCreationMode(false);
                    setDocumentForAISummary(null);
                    setAnalysisLoading(false);
                  },
                  handleAnalyzeError: undefined,
                  isDocumentContract: Boolean(contractGenerated),
                  documentId,
                })
              }
            />
          )}
          {analysisLoading && <PacmanLoaderBlock loading={analysisLoading} />}
        </Box>
        {(pendingStatus || pendingStatusAISummary) && (
          <ProgressOverlay className='position-absolute' />
        )}
      </Box>
      {planUpgradingMessageAISummary && (
        <MessageModal
          open={planUpgradingMessageAISummary}
          modalMessage={t('messages.monthLimitReached')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => navigate(ROUTES.PRICING)}
          goBackButtonText={t('buttons.later')}
          onClose={() => {
            setPlanUpgradingMessageAISummary(false);
          }}
        />
      )}
    </>
  );
};

export default AISummaryDetails;
